import Papa from 'papaparse';
import { FileWithPath } from '@mantine/dropzone';

export interface CSVPreviewData {
  headers: string[];
  rows: string[][];
  totalRows: number;
}

export interface ColumnMapping {
  [csvColumn: string]: string; // Maps CSV column name to field name
}

export interface ProcessedCSVResult {
  csvPreview: CSVPreviewData;
  fullCsvData: { headers: string[]; rows: string[][] };
  columnMapping: ColumnMapping;
}

/**
 * Parse CSV file using PapaParse
 * Extracted from CSVUploadModal for reuse
 */
export const parseCSVFile = async (
  file: FileWithPath,
): Promise<{
  headers: string[];
  allRows: string[][];
  previewRows: string[][];
}> => {
  const text = await file.text();

  return new Promise((resolve, reject) => {
    // Use PapaParse to properly handle CSV parsing with quoted fields
    Papa.parse(text, {
      header: false, // We'll handle headers manually to maintain existing logic
      skipEmptyLines: true,
      complete: (result) => {
        try {
          const allParsedRows = result.data as string[][];

          if (allParsedRows.length < 1) {
            reject(new Error('CSV file must contain at least a header row'));
            return;
          }

          // Extract headers from first row
          const headers = allParsedRows[0].map((h) => h.trim());
          const dataRows = allParsedRows.slice(1);

          // Filter out empty rows and normalize data (data rows optional)
          const allRows: string[][] = [];
          const previewRows: string[][] = [];

          dataRows.forEach((row) => {
            // Check if row has any non-empty values
            if (row.length > 0 && row.some((v) => v && v.trim().length > 0)) {
              // Pad the row to match header length
              const normalizedRow = [...row];
              while (normalizedRow.length < headers.length) {
                normalizedRow.push('');
              }
              // Trim to header length
              const finalRow = normalizedRow.slice(0, headers.length).map((v) => v || '');
              allRows.push(finalRow);

              // Only add to preview if we haven't reached the limit (50 rows)
              if (previewRows.length < 50) {
                previewRows.push(finalRow);
              }
            }
          });

          resolve({ headers, allRows, previewRows });
        } catch (error) {
          console.error('Error processing parsed CSV:', error);
          reject(new Error('Failed to process CSV data. Please check the format.'));
        }
      },
      error: (error: any) => {
        console.error('PapaParse error:', error);
        reject(new Error('Failed to parse CSV file. Please check the format.'));
      },
    });
  });
};

/**
 * Process CSV data with smart auto-detection of column mappings
 * Extracted from CSVUploadModal for reuse
 */
export const processCSVData = (
  headers: string[],
  allRows: string[][],
  previewRows: string[][],
): ProcessedCSVResult => {
  // Create initial column mapping with smart auto-detection
  const initialMapping: ColumnMapping = {};
  headers.forEach((header) => {
    const lowerHeader = header.toLowerCase();

    // Match specific patterns for survey data
    if (lowerHeader.includes('response id') || lowerHeader === 'response id') {
      initialMapping[header] = 'originId';
    } else if (lowerHeader.includes('recipient email') || lowerHeader === 'recipient email') {
      initialMapping[header] = 'submitterEmail';
    } else if (lowerHeader.includes('recorded date') || lowerHeader === 'recorded date') {
      initialMapping[header] = 'createdAt';
    } else if (
      lowerHeader.includes('external data reference') ||
      lowerHeader === 'external data reference'
    ) {
      initialMapping[header] = 'linkedAccountId';
    }
    // General patterns
    else if (
      lowerHeader.includes('id') &&
      (lowerHeader.includes('origin') || lowerHeader.includes('response'))
    ) {
      initialMapping[header] = 'originId';
    } else if (lowerHeader === 'title' || lowerHeader.includes('title')) {
      initialMapping[header] = 'title';
    } else if (lowerHeader === 'description' || lowerHeader.includes('description')) {
      initialMapping[header] = 'description';
    } else if (
      lowerHeader.includes('created') ||
      lowerHeader.includes('date') ||
      lowerHeader.includes('recorded')
    ) {
      initialMapping[header] = 'createdAt';
    } else if (lowerHeader.includes('account') && lowerHeader.includes('id')) {
      initialMapping[header] = 'linkedAccountId';
    } else if (lowerHeader.includes('email')) {
      initialMapping[header] = 'submitterEmail';
    } else if (lowerHeader.includes('priority')) {
      initialMapping[header] = 'priority';
    }
    // For survey questions, if they're descriptive, consider them as title or description
    else if (
      lowerHeader.includes('satisfied') ||
      lowerHeader.includes('likely') ||
      lowerHeader.includes('?')
    ) {
      // First question-like column could be title
      const alreadyHasTitle = Object.values(initialMapping).includes('title');
      if (!alreadyHasTitle) {
        initialMapping[header] = 'title';
      } else {
        initialMapping[header] = ''; // Skip additional questions for now
      }
    } else {
      // Default to empty (skip) for unknown columns
      initialMapping[header] = '';
    }
  });

  return {
    csvPreview: { headers, rows: previewRows, totalRows: allRows.length },
    fullCsvData: { headers, rows: allRows },
    columnMapping: initialMapping,
  };
};
